'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface AssessmentSection {
  id: string;
  title: string;
  description: string;
  regulation: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  weight: number;
}

interface Answers {
  [key: string]: boolean | null;
}

const sections: AssessmentSection[] = [
  {
    id: 'administrative',
    title: 'Administrative Safeguards',
    description: '45 CFR § 164.308 - Policies and procedures',
    regulation: '§164.308',
    questions: [
      { id: 'a1', text: 'Do you have a designated Security Officer?', weight: 10 },
      { id: 'a2', text: 'Do you have a designated Privacy Officer?', weight: 10 },
      { id: 'a3', text: 'Have you conducted a security risk assessment in the past year?', weight: 10 },
      { id: 'a4', text: 'Do you have documented security policies and procedures?', weight: 9 },
      { id: 'a5', text: 'Do you have a workforce training program on HIPAA?', weight: 9 },
      { id: 'a6', text: 'Are background checks performed on employees with PHI access?', weight: 7 },
      { id: 'a7', text: 'Do you have sanctions policies for HIPAA violations?', weight: 7 },
      { id: 'a8', text: 'Do you have an incident response and reporting procedure?', weight: 9 },
      { id: 'a9', text: 'Do you maintain a contingency plan for emergencies?', weight: 8 },
      { id: 'a10', text: 'Do you evaluate effectiveness of security measures regularly?', weight: 8 },
    ],
  },
  {
    id: 'physical',
    title: 'Physical Safeguards',
    description: '45 CFR § 164.310 - Physical access controls',
    regulation: '§164.310',
    questions: [
      { id: 'p1', text: 'Do you have facility access controls (keycards, badges)?', weight: 9 },
      { id: 'p2', text: 'Are workstations with PHI access in secure areas?', weight: 8 },
      { id: 'p3', text: 'Do you have policies for workstation use and security?', weight: 7 },
      { id: 'p4', text: 'Do you track and control access to devices containing PHI?', weight: 8 },
      { id: 'p5', text: 'Do you have procedures for disposal of PHI media?', weight: 9 },
      { id: 'p6', text: 'Do you have procedures for reuse of media containing PHI?', weight: 7 },
      { id: 'p7', text: 'Are backup media stored securely and encrypted?', weight: 8 },
    ],
  },
  {
    id: 'technical',
    title: 'Technical Safeguards',
    description: '45 CFR § 164.312 - Technology controls',
    regulation: '§164.312',
    questions: [
      { id: 't1', text: 'Do you have unique user identification for all users?', weight: 10 },
      { id: 't2', text: 'Do you have emergency access procedures?', weight: 8 },
      { id: 't3', text: 'Do you have automatic logoff after inactivity?', weight: 7 },
      { id: 't4', text: 'Do you encrypt PHI at rest?', weight: 10 },
      { id: 't5', text: 'Do you encrypt PHI in transit?', weight: 10 },
      { id: 't6', text: 'Do you maintain audit logs of PHI access?', weight: 9 },
      { id: 't7', text: 'Do you have integrity controls to prevent unauthorized alteration?', weight: 8 },
      { id: 't8', text: 'Do you authenticate users before granting PHI access?', weight: 9 },
      { id: 't9', text: 'Do you use multi-factor authentication for remote access?', weight: 8 },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Rule Compliance',
    description: '45 CFR § 164.500 - Use and disclosure of PHI',
    regulation: '§164.500',
    questions: [
      { id: 'pr1', text: 'Do you have a Notice of Privacy Practices?', weight: 10 },
      { id: 'pr2', text: 'Do patients acknowledge receipt of the Notice?', weight: 8 },
      { id: 'pr3', text: 'Do you obtain authorization for uses beyond treatment/payment/operations?', weight: 9 },
      { id: 'pr4', text: 'Can patients request access to their PHI?', weight: 9 },
      { id: 'pr5', text: 'Can patients request amendments to their PHI?', weight: 8 },
      { id: 'pr6', text: 'Do you provide an accounting of disclosures when requested?', weight: 8 },
      { id: 'pr7', text: 'Do you honor requests for confidential communications?', weight: 7 },
      { id: 'pr8', text: 'Do you apply the minimum necessary standard?', weight: 8 },
    ],
  },
  {
    id: 'breach',
    title: 'Breach Notification',
    description: '45 CFR § 164.400 - Breach notification requirements',
    regulation: '§164.400',
    questions: [
      { id: 'b1', text: 'Can you detect breaches of unsecured PHI?', weight: 9 },
      { id: 'b2', text: 'Do you have procedures to assess breach risk?', weight: 8 },
      { id: 'b3', text: 'Can you notify affected individuals within 60 days?', weight: 10 },
      { id: 'b4', text: 'Can you notify HHS as required (immediate or annual)?', weight: 9 },
      { id: 'b5', text: 'Do you notify media for breaches affecting 500+ individuals?', weight: 7 },
      { id: 'b6', text: 'Do you document all breach assessments and notifications?', weight: 8 },
    ],
  },
  {
    id: 'baa',
    title: 'Business Associate Agreements',
    description: '45 CFR § 164.308(b) - Third-party requirements',
    regulation: '§164.308(b)',
    questions: [
      { id: 'ba1', text: 'Do you have BAAs with all business associates?', weight: 10 },
      { id: 'ba2', text: 'Do BAAs include all required provisions?', weight: 9 },
      { id: 'ba3', text: 'Do you verify BA compliance before engagement?', weight: 8 },
      { id: 'ba4', text: 'Do you maintain an inventory of all business associates?', weight: 7 },
      { id: 'ba5', text: 'Do you monitor BA compliance and security practices?', weight: 7 },
      { id: 'ba6', text: 'Do subcontractors have appropriate agreements?', weight: 8 },
    ],
  },
];

export default function HIPAACompliancePage() {
  const [view, setView] = useState<'landing' | 'assessment' | 'results'>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'hipaa-compliance',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    let totalWeight = 0;
    let earnedWeight = 0;

    const sectionScores = sections.map((section) => {
      let sectionTotal = 0;
      let sectionEarned = 0;

      section.questions.forEach((q) => {
        sectionTotal += q.weight;
        totalWeight += q.weight;
        if (answers[q.id] === true) {
          sectionEarned += q.weight;
          earnedWeight += q.weight;
        }
      });

      return {
        id: section.id,
        title: section.title,
        regulation: section.regulation,
        score: sectionTotal > 0 ? Math.round((sectionEarned / sectionTotal) * 100) : 0,
        gaps: section.questions.filter((q) => answers[q.id] !== true),
      };
    });

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      sectionScores,
    };
  };

  const results = view === 'results' ? calculateResults() : null;

  const handleSaveToCommandCenter = async () => {
    if (!results || !commandCenterEnabled) return;
    const scores: Record<string, number> = {};
    results.sectionScores.forEach(s => { scores[s.id] = s.score; });
    const gaps = results.sectionScores.flatMap(section =>
      section.gaps.map(gap => ({
        id: gap.id,
        title: gap.text,
        severity: section.score < 40 ? 'critical' as const : section.score < 60 ? 'high' as const : 'medium' as const,
        description: `Gap in ${section.title}`,
      }))
    );
    const assessment = await saveResult({
      name: `HIPAA Compliance - ${new Date().toLocaleDateString()}`,
      score_overall: results.overallScore,
      scores,
      answers,
      gaps,
    });
    if (assessment) setSaved(true);
  };

  const exportReport = () => {
    if (!results) return;

    const lines = [
      '# HIPAA Compliance Assessment Report',
      '',
      `**Date:** ${new Date().toLocaleDateString()}`,
      `**Overall Score:** ${results.overallScore}%`,
      '',
      '---',
      '',
      '## Compliance by Category',
      '',
      ...results.sectionScores.map((s) => `- **${s.title} (${s.regulation}):** ${s.score}%`),
      '',
      '## Compliance Gaps',
      '',
    ];

    results.sectionScores.forEach((section) => {
      if (section.gaps.length > 0) {
        lines.push(`### ${section.title}`);
        lines.push('');
        section.gaps.forEach((gap) => {
          lines.push(`- [ ] ${gap.text}`);
        });
        lines.push('');
      }
    });

    lines.push('## Penalty Reference');
    lines.push('');
    lines.push('| Tier | Culpability | Penalty Range |');
    lines.push('|------|-------------|---------------|');
    lines.push('| 1 | Unknowing | $100-$50,000 per violation |');
    lines.push('| 2 | Reasonable cause | $1,000-$50,000 per violation |');
    lines.push('| 3 | Willful neglect (corrected) | $10,000-$50,000 per violation |');
    lines.push('| 4 | Willful neglect (not corrected) | $50,000 per violation |');
    lines.push('');
    lines.push('Annual maximum: $1.5 million per violation category');
    lines.push('');
    lines.push('---');
    lines.push('*Generated by The Art of CTO - HIPAA Compliance Tool*');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hipaa-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-accent';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">HIPAA Compliance</span>
        </nav>

        {view === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
                45 CFR Parts 160 & 164
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                HIPAA Compliance Assessment
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Assess your organization&apos;s compliance with HIPAA Security, Privacy, and Breach
                Notification Rules.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">🔒</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Security Rule</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Administrative, physical, and technical safeguards
                </p>
              </div>
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">👤</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Privacy Rule</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Use and disclosure of protected health information
                </p>
              </div>
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">🚨</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Breach Notification</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Incident detection, assessment, and notification
                </p>
              </div>
            </div>

            <div className="text-center mb-12">
              <button
                onClick={() => setView('assessment')}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors"
              >
                Start Assessment
              </button>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
                50+ requirements across 6 categories
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Who Must Comply?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Covered Entities</h3>
                  <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                    <li>• Health care providers</li>
                    <li>• Health plans</li>
                    <li>• Health care clearinghouses</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Business Associates</h3>
                  <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                    <li>• IT vendors with PHI access</li>
                    <li>• Cloud service providers</li>
                    <li>• Billing and claims processors</li>
                    <li>• EHR vendors</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {view === 'assessment' && (
          <>
            <div className="mb-8">
              <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-tertiary mb-2">
                <span>{sections[currentSection].title}</span>
                <span>Section {currentSection + 1} of {sections.length}</span>
              </div>
              <div className="h-2 bg-light-border dark:bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm font-mono">
                  {sections[currentSection].regulation}
                </span>
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  {sections[currentSection].title}
                </h2>
              </div>
              <p className="text-light-text-secondary dark:text-foreground-secondary mb-8">
                {sections[currentSection].description}
              </p>

              <div className="space-y-6">
                {sections[currentSection].questions.map((question) => (
                  <div key={question.id} className="border-b border-light-border dark:border-border-muted pb-6">
                    <p className="text-light-text dark:text-foreground mb-3">{question.text}</p>
                    <div className="flex gap-4">
                      <button
                        onClick={() => handleAnswer(question.id, true)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === true
                            ? 'bg-green-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-green-500/20'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => handleAnswer(question.id, false)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === false
                            ? 'bg-red-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-red-500/20'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : setView('landing')}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                {currentSection > 0 ? '← Previous' : '← Back'}
              </button>
              <button
                onClick={() => {
                  if (currentSection < sections.length - 1) {
                    setCurrentSection(currentSection + 1);
                  } else {
                    setView('results');
                  }
                }}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                {currentSection < sections.length - 1 ? 'Next Section →' : 'View Results →'}
              </button>
            </div>
          </>
        )}

        {view === 'results' && results && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">HIPAA Compliance Results</h1>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.overallScore)}`}>
                {results.overallScore}%
              </div>
              <p className="text-light-text-secondary dark:text-foreground-secondary">
                {results.overallScore >= 80 ? 'Strong compliance posture' :
                 results.overallScore >= 60 ? 'Moderate compliance - address gaps' :
                 'Significant remediation needed'}
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Compliance by Rule</h2>
              <div className="space-y-4">
                {results.sectionScores.map((section) => (
                  <div key={section.id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-light-text dark:text-foreground">
                        <span className="font-mono text-red-400 mr-2">{section.regulation}</span>
                        {section.title}
                      </span>
                      <span className={getScoreColor(section.score)}>{section.score}%</span>
                    </div>
                    <div className="h-3 bg-light-border dark:bg-background rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          section.score >= 80 ? 'bg-green-500' :
                          section.score >= 60 ? 'bg-yellow-500' :
                          section.score >= 40 ? 'bg-accent' : 'bg-red-500'
                        }`}
                        style={{ width: `${section.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Priority Remediation</h2>
              {results.sectionScores
                .filter((s) => s.gaps.length > 0)
                .sort((a, b) => a.score - b.score)
                .slice(0, 3)
                .map((section) => (
                  <div key={section.id} className="mb-6 last:mb-0">
                    <h3 className="font-semibold text-accent mb-3">{section.title}</h3>
                    <ul className="space-y-2">
                      {section.gaps.slice(0, 3).map((gap) => (
                        <li key={gap.id} className="flex items-start gap-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
                          <span className="text-red-400 mt-0.5">○</span>
                          {gap.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={exportReport}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                Export Report
              </button>
              {commandCenterEnabled && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving || saved}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
                </button>
              )}
              <button
                onClick={() => {
                  setView('landing');
                  setCurrentSection(0);
                  setAnswers({});
                  setSaved(false);
                }}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                Start Over
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
